import React, { useState, useEffect } from 'react';
import API from '../api';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Enregistrer les composants nécessaires pour Chart.js
Chart.register(...registerables);

function AdminDashboard() {
  // État existant pour les licences
  const [licenses, setLicenses] = useState([]);
  const [error, setError] = useState('');
  const [newLicense, setNewLicense] = useState({
    licenseKey: '',
    status: 'Actif',
    expirationDate: '',
    departementsAutorises: '',
    initialCredits: 0,
  });
  const [message, setMessage] = useState('');
  
  // Nouveaux états pour les utilisateurs et les statistiques
  const [users, setUsers] = useState([]);
  const [systemStats, setSystemStats] = useState({
    users_count: 0,
    active_users_7d: 0,
    new_users_30d: 0,
    total_extractions: 0,
    average_response_time: 0,
    error_rate: 0
  });
  const [errors, setErrors] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [userFilter, setUserFilter] = useState('');
  const [licenseFilter, setLicenseFilter] = useState('');
  
  // État pour le chargement
  const [loading, setLoading] = useState({
    licenses: false,
    users: false,
    stats: false,
    errors: false,
    performance: false
  });

  useEffect(() => {
    // Chargement initial des données
    fetchLicenses();
    fetchUsers();
    fetchSystemStats();
    fetchErrors();
    fetchPerformance();
    
    // Configurer un rafraîchissement périodique des données
    const refreshInterval = setInterval(() => {
      if (activeTab === 0) fetchLicenses();
      if (activeTab === 1) fetchUsers();
      if (activeTab === 2) {
        fetchSystemStats();
        fetchErrors();
        fetchPerformance();
      }
    }, 60000); // Rafraîchir toutes les minutes
    
    return () => clearInterval(refreshInterval);
  }, [activeTab]);

  // Fonctions existantes
  const fetchLicenses = async () => {
    setLoading(prev => ({ ...prev, licenses: true }));
    try {
      const response = await API.get('/api/admin/licenses');
      setLicenses(response.data.licenses || []);
    } catch (err) {
      setError('Erreur lors du chargement des licences. Veuillez réessayer.');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, licenses: false }));
    }
  };
  
  const formatDateForBackend = (dateString) => {
    // Convertit le format YYYY-MM-DD de l'input date en DD-MM-YYYY pour le backend
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  const handleAddLicense = async () => {
    setError('');
    setMessage('');
    
    try {
      const licenseData = {
        ...newLicense,
        expirationDate: formatDateForBackend(newLicense.expirationDate),
        departementsAutorises: newLicense.departementsAutorises.split(',').map(dep => dep.trim()),
        initialCredits: Number(newLicense.initialCredits),
      };
      
      const response = await API.post('/api/admin/licenses', licenseData);
      setMessage("Licence ajoutée avec succès.");
      setNewLicense({
        licenseKey: '',
        status: 'Actif',
        expirationDate: '',
        departementsAutorises: '',
        initialCredits: 0,
      });
      fetchLicenses();
    } catch (err) {
      console.error("Erreur d'ajout de licence:", err);
      setError(err.response?.data?.detail || "Erreur lors de l'ajout de la licence.");
    }
  };
  
  const handleDeleteLicense = async (licenseId) => {
    if (!window.confirm("Confirmez-vous la suppression de cette licence ?")) return;
    setError('');
    setMessage('');
    
    try {
      await API.delete(`/api/admin/licenses/${licenseId}`);
      setMessage("Licence supprimée avec succès.");
      fetchLicenses();
    } catch (err) {
      console.error("Erreur de suppression de licence:", err);
      setError(err.response?.data?.detail || "Erreur lors de la suppression de la licence.");
    }
  };
  
  const handleResetCredits = async (licenseId) => {
    if (!window.confirm("Confirmez-vous la réinitialisation des crédits pour cette licence ?")) return;
    setError('');
    setMessage('');
    
    try {
      await API.post(`/api/admin/licenses/${licenseId}/reset`);
      setMessage("Crédits réinitialisés avec succès.");
      fetchLicenses();
    } catch (err) {
      console.error("Erreur de réinitialisation des crédits:", err);
      setError(err.response?.data?.detail || "Erreur lors de la réinitialisation des crédits.");
    }
  };
  
  // Nouvelles fonctions pour la gestion des utilisateurs et statistiques
  const fetchUsers = async () => {
    setLoading(prev => ({ ...prev, users: true }));
    try {
      const params = new URLSearchParams();
      if (userFilter) params.append('search', userFilter);
      if (licenseFilter) params.append('license_key', licenseFilter);
      
      const response = await API.get(`/api/admin/users?${params.toString()}`);
      setUsers(response.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs. Veuillez réessayer.');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };
  
  const fetchSystemStats = async () => {
    setLoading(prev => ({ ...prev, stats: true }));
    try {
      const response = await API.get('/api/admin/stats/system');
      setSystemStats(response.data || {});
    } catch (err) {
      setError('Erreur lors du chargement des statistiques système. Veuillez réessayer.');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  };
  
  const fetchErrors = async () => {
    setLoading(prev => ({ ...prev, errors: true }));
    try {
      const response = await API.get('/api/admin/errors?days=7');
      setErrors(response.data.errors || []);
    } catch (err) {
      setError('Erreur lors du chargement des journaux d\'erreurs. Veuillez réessayer.');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, errors: false }));
    }
  };
  
  const fetchPerformance = async () => {
    setLoading(prev => ({ ...prev, performance: true }));
    try {
      const response = await API.get('/api/admin/performance?days=7');
      setPerformance(response.data.metrics || []);
    } catch (err) {
      setError('Erreur lors du chargement des métriques de performance. Veuillez réessayer.');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, performance: false }));
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.")) {
      return;
    }
    
    try {
      await API.delete(`/api/admin/users/${userId}`);
      setMessage('Utilisateur supprimé avec succès.');
      fetchUsers(); // Rafraîchir la liste
    } catch (err) {
      setError('Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer.');
      console.error(err);
    }
  };
  
  // Préparation des données pour les graphiques
  const preparePerformanceChartData = () => {
    if (!performance.length) return null;
    
    // Trier les données par timestamp
    const sortedData = [...performance].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    
    // Extraire les labels (dates) et les données de temps de réponse
    const labels = sortedData.map(item => 
      format(parseISO(item.timestamp), 'dd/MM HH:mm', { locale: fr })
    );
    
    const responseTimeData = sortedData.map(item => item.response_time);
    
    return {
      labels,
      datasets: [{
        label: 'Temps de réponse (ms)',
        data: responseTimeData,
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.4
      }]
    };
  };
  
  const prepareErrorChartData = () => {
    // Grouper les erreurs par type
    const errorTypes = {};
    errors.forEach(error => {
      const type = error.type || 'Inconnu';
      if (!errorTypes[type]) errorTypes[type] = 0;
      errorTypes[type]++;
    });
    
    return {
      labels: Object.keys(errorTypes),
      datasets: [{
        label: 'Erreurs par type',
        data: Object.values(errorTypes),
        backgroundColor: [
          'rgba(255, 99, 132, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(75, 192, 192, 0.2)',
          'rgba(153, 102, 255, 0.2)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1
      }]
    };
  };
  
  const prepareUserActivityData = () => {
    return {
      labels: ['Utilisateurs actifs', 'Utilisateurs inactifs'],
      datasets: [{
        label: 'Activité des utilisateurs',
        data: [
          systemStats.active_users_7d,
          systemStats.users_count - systemStats.active_users_7d
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.2)',
          'rgba(255, 99, 132, 0.2)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }]
    };
  };
  
  // Rendu du composant
  return (
    <div className="admin-dashboard-container">
      <h1>Dashboard Administrateur ProspXion</h1>
      
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      
      <Tabs selectedIndex={activeTab} onSelect={index => setActiveTab(index)}>
        <TabList>
          <Tab>Gestion des Licences</Tab>
          <Tab>Gestion des Utilisateurs</Tab>
          <Tab>Statistiques & Monitoring</Tab>
        </TabList>
        
        {/* Onglet Gestion des Licences */}
        <TabPanel>
          <section className="add-license-section">
            <h3>Ajouter une Licence</h3>
            <div className="form-group">
              <label>Licence :</label>
              <input
                type="text"
                value={newLicense.licenseKey}
                onChange={(e) => setNewLicense({ ...newLicense, licenseKey: e.target.value })}
                placeholder="Clé de licence"
              />
            </div>
            <div className="form-group">
              <label>Statut :</label>
              <select
                value={newLicense.status}
                onChange={(e) => setNewLicense({ ...newLicense, status: e.target.value })}
              >
                <option value="Actif">Actif</option>
                <option value="Inactif">Inactif</option>
                <option value="Demo">Demo</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date d'expiration :</label>
              <input
                type="date"
                value={newLicense.expirationDate}
                onChange={(e) => setNewLicense({ ...newLicense, expirationDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Départements autorisés (séparés par des virgules) :</label>
              <input
                type="text"
                value={newLicense.departementsAutorises}
                onChange={(e) =>
                  setNewLicense({ ...newLicense, departementsAutorises: e.target.value })
                }
                placeholder="Ex: 34, 11, 30"
              />
            </div>
            <div className="form-group">
              <label>Crédits initiaux :</label>
              <input
                type="number"
                value={newLicense.initialCredits}
                onChange={(e) =>
                  setNewLicense({ ...newLicense, initialCredits: e.target.value })
                }
                min="0"
              />
            </div>
            <button className="action-button" onClick={handleAddLicense}>
              Ajouter la Licence
            </button>
          </section>
          
          <section className="licenses-section">
            <h3>Liste des Licences {loading.licenses && <span className="loading-indicator">Chargement...</span>}</h3>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Licence</th>
                  <th>Statut</th>
                  <th>Expiration</th>
                  <th>Départements Autorisés</th>
                  <th>Crédits Initiaux</th>
                  <th>Crédits Actuels</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((lic) => (
                  <tr key={lic.id}>
                    <td>{lic.id}</td>
                    <td>{lic.licenseKey}</td>
                    <td>{lic.status}</td>
                    <td>{lic.expirationDate}</td>
                    <td>{Array.isArray(lic.departementsAutorises) ? lic.departementsAutorises.join(', ') : lic.departementsAutorises}</td>
                    <td>{lic.initialCredits}</td>
                    <td>{lic.currentCredits}</td>
                    <td>
                      <button className="reset-button" onClick={() => handleResetCredits(lic.id)}>Réinitialiser</button>
                      <button className="delete-button" onClick={() => handleDeleteLicense(lic.id)}>Supprimer</button>
                    </td>
                  </tr>
                ))}
                {licenses.length === 0 && (
                  <tr>
                    <td colSpan="8" className="no-data">
                      Aucune licence trouvée
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </TabPanel>
        
        {/* Onglet Gestion des Utilisateurs */}
        <TabPanel>
          <h2>Gestion des Utilisateurs {loading.users && <span className="loading-indicator">Chargement...</span>}</h2>
          
          <div className="filter-controls">
            <div className="filter-form">
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="filter-input"
              />
              
              <select
                value={licenseFilter}
                onChange={(e) => setLicenseFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">Toutes les licences</option>
                {licenses.map(license => (
                  <option key={license.id} value={license.licenseKey}>
                    {license.licenseKey}
                  </option>
                ))}
              </select>
              
              <button onClick={fetchUsers} className="filter-button">
                Filtrer
              </button>
            </div>
          </div>
          
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Entreprise</th>
                  <th>Licence</th>
                  <th>Dernière connexion</th>
                  <th>Extractions</th>
                  <th>Crédits utilisés</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>{user.entreprise}</td>
                    <td>{user.license_key}</td>
                    <td>
                      {user.derniere_connexion 
                        ? format(parseISO(user.derniere_connexion), 'dd/MM/yyyy HH:mm', { locale: fr })
                        : 'Jamais'}
                    </td>
                    <td>{user.extractions_count}</td>
                    <td>{user.credits_utilises}</td>
                    <td>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="delete-button"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="7" className="no-data">
                      Aucun utilisateur trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabPanel>
        
        {/* Onglet Statistiques & Monitoring */}
        <TabPanel>
          <h2>Statistiques Système {loading.stats && <span className="loading-indicator">Chargement...</span>}</h2>
          
          <div className="stats-dashboard">
            <div className="stats-cards">
              <div className="stats-card">
                <h3>Utilisateurs</h3>
                <div className="stat-value">{systemStats.users_count}</div>
                <div className="stat-label">Total</div>
              </div>
              
              <div className="stats-card">
                <h3>Utilisateurs Actifs</h3>
                <div className="stat-value">{systemStats.active_users_7d}</div>
                <div className="stat-label">7 derniers jours</div>
              </div>
              
              <div className="stats-card">
                <h3>Nouveaux Utilisateurs</h3>
                <div className="stat-value">{systemStats.new_users_30d}</div>
                <div className="stat-label">30 derniers jours</div>
              </div>
              
              <div className="stats-card">
                <h3>Extractions</h3>
                <div className="stat-value">{systemStats.total_extractions}</div>
                <div className="stat-label">Total</div>
              </div>
              
              <div className="stats-card">
                <h3>Temps de Réponse</h3>
                <div className="stat-value">{systemStats.average_response_time.toFixed(2)} ms</div>
                <div className="stat-label">Moyenne</div>
              </div>
              
              <div className="stats-card">
                <h3>Taux d'Erreur</h3>
                <div className="stat-value">{(systemStats.error_rate * 100).toFixed(2)}%</div>
                <div className="stat-label">Global</div>
              </div>
            </div>
            
            <div className="charts-container">
              <div className="chart-wrapper">
                <h3>Performance (Temps de réponse)</h3>
                {performance.length > 0 ? (
                  <Line 
                    data={preparePerformanceChartData()} 
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: true,
                          text: 'Temps de réponse des 7 derniers jours'
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="no-data">Aucune donnée disponible</div>
                )}
              </div>
              
              <div className="chart-wrapper">
                <h3>Répartition des Erreurs</h3>
                {errors.length > 0 ? (
                  <Pie 
                    data={prepareErrorChartData()} 
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: true,
                          text: 'Répartition des erreurs par type'
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="no-data">Aucune erreur enregistrée</div>
                )}
              </div>
              
              <div className="chart-wrapper">
                <h3>Activité des Utilisateurs</h3>
                <Pie 
                  data={prepareUserActivityData()} 
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Activité des utilisateurs (7 derniers jours)'
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="logs-section">
            <h3>Journal des Erreurs Récentes {loading.errors && <span className="loading-indicator">Chargement...</span>}</h3>
            
            <div className="logs-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Sévérité</th>
                    <th>Message</th>
                    <th>Utilisateur</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((error, index) => (
                    <tr key={error.id || index} className={`severity-${error.severity}`}>
                      <td>{format(parseISO(error.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}</td>
                      <td>{error.type || 'Inconnu'}</td>
                      <td>{error.severity || 'info'}</td>
                      <td><div className="error-message-cell">{error.message}</div></td>
                      <td>{error.user_id || 'Anonyme'}</td>
                    </tr>
                  ))}
                  {errors.length === 0 && (
                    <tr>
                      <td colSpan="5" className="no-data">
                        Aucune erreur enregistrée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}

export default AdminDashboard;
